import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { createPageUrl } from '@/utils';
import { auditDiyPlaybookStepAssets, logDiyPlaybookClone } from '@/lib/diyPlaybookCloneLog';
import { cloneDiyPlaybook } from '@/lib/cloneDiyPlaybook';
import { isAccountExcludedFromPlaybooks } from '@/lib/tenantUtils';
import {
  buildDiyPreviewPayload,
  diyAccountIdForWire,
  diyAccountsListTruncation,
  fitScoreBounds,
  previewEmptyReason,
} from '@/lib/diyPlaybookPreview';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Plus, 
  Trash2, 
  Mail, 
  Linkedin, 
  Phone, 
  ChevronDown, 
  ChevronUp,
  AlertTriangle,
  Loader2,
  X,
  Users,
  Building2,
  RefreshCw,
  Globe,
  Copy,
} from 'lucide-react';
import {
  buildDiyStepAssetCatalogFromFormSettings,
  sanitizeDiyFormValuesAgainstCatalog,
  sanitizeDiyStepsArray,
} from '@/lib/diyStepCatalogSanitize';
import { useTranslation } from '@/components/utils/translations';
import { useUser } from '@/contexts/UserContext';
import {
  filterAssetsForDiyProductAlignment,
  isDiyProductAlignmentDebugEnabled,
  normalizeEducationalResourceRow,
  normalizeProductAlignmentTag,
  resolveSelectedProductKeysForDiyAlignment,
} from '@/lib/educationalResources';
import {
  CAYUSE_KEYS,
  getProductDisplayNameForTenant,
  isPlaceholderProductName,
} from '@/lib/accountTargetProducts';
import LinkedInSubtypeBadge from '@/components/common/LinkedInSubtypeBadge';
import {
  buildDiyStepChannelSelectOptions,
  canOfferLinkedInConnectionRequest,
  channelMatchesEnabledChannels,
  enforceLinkedInChannelSequence,
  isLinkedInOutboxChannel,
  logLinkedInChannelResolution,
  normalizeLinkedInStepChannelValue,
  resolveDiyStepChannelFromApiStep,
  resolveDiyStepLinkedInChannel,
} from '@/lib/outboxChannel';

// Helper to normalize country data - handles both string and {value, label} formats
const normalizeCountries = (countries) => {
  if (!countries || !Array.isArray(countries)) return [];
  return countries.map(c => {
    if (typeof c === 'string') {
      return { value: c, label: c };
    }
    if (typeof c === 'object' && c !== null) {
      return {
        value: String(c.value || c.id || c.code || c.key || ''),
        label: String(c.label || c.name || c.value || c.id || '')
      };
    }
    return { value: String(c), label: String(c) };
  }).filter(c => c.value);
};

// Helper to normalize sector data - handles both string and {value, label} formats
const normalizeSectors = (sectors) => {
  if (!sectors || !Array.isArray(sectors)) return [];
  return sectors.map(s => {
    if (typeof s === 'string') {
      return { value: s, label: s };
    }
    if (typeof s === 'object' && s !== null) {
      return {
        value: String(s.value || s.id || s.key || ''),
        label: String(s.label || s.name || s.value || s.id || '')
      };
    }
    return { value: String(s), label: String(s) };
  }).filter(s => s.value);
};

// Helper to normalize array data for dropdowns
const normalizeOptions = (options, useNameAsId = false) => {
  if (!options || !Array.isArray(options)) return [];
  return options.map((opt, index) => {
    if (typeof opt === 'string') {
      return { id: opt, label: opt };
    }
    if (typeof opt === 'object' && opt !== null) {
      // For product links, ALWAYS use name as ID (ignore any synthetic id field)
      const id = useNameAsId 
        ? String(opt.name || opt.label || `item_${index}`)
        : String(
            opt.id ||
              opt.key ||
              opt.value ||
              opt.name ||
              opt.hook ||
              `item_${index}`,
          );
      const trimmedName = opt.name != null && String(opt.name).trim() !== '' ? String(opt.name).trim() : '';
      const nameFromLabel =
        !trimmedName && opt.label != null && String(opt.label).trim() !== ''
          ? String(opt.label).trim()
          : '';
      const storedName = trimmedName || nameFromLabel;
      const pkRaw = opt.product_key ?? opt.productKey ?? opt.product_context;
      const productKey =
        pkRaw != null && String(pkRaw).trim() !== ''
          ? normalizeProductAlignmentTag(pkRaw)
          : undefined;
      return {
        id,
        label: String(opt.label || opt.name || opt.hook || opt.id || opt.key || ''),
        ...(storedName ? { name: storedName } : {}),
        ...(opt.type ? { type: opt.type } : {}),
        ...(opt.url ? { url: opt.url } : {}),
        ...(productKey && productKey !== 'ALL'
          ? { productKey, product_key: productKey }
          : {}),
      };
    }
    return { id: String(opt), label: String(opt) };
  }).filter(opt => opt.id);
};

/** DIY → BE: `product_link_keys` must be exact trimmed `product_links[].name` (admin_atoms). */
function canonicalProductLinkName(row) {
  if (!row || typeof row !== 'object') return '';
  if (row.name != null && String(row.name).trim() !== '') return String(row.name).trim();
  return '';
}

function buildProductLinkAliasToCanonicalMap(rows) {
  const m = new Map();
  for (const row of rows || []) {
    const name = canonicalProductLinkName(row);
    if (!name) continue;
    m.set(name, name);
    const id = row.id != null ? String(row.id).trim() : '';
    if (id && id !== name) m.set(id, name);
  }
  return m;
}

function hasDiyProductSelection(selectedProductKeys) {
  return (selectedProductKeys?.length || 0) > 0;
}

function DiyProductAlignmentFilterHint({
  selectedProductKeys,
  filteredCount,
  totalCount,
  label,
  tenantHasProducts = true,
}) {
  if (!tenantHasProducts) return null;
  const selectedLen = selectedProductKeys?.length || 0;
  if (selectedLen === 0) {
    return (
      <p className="text-xs text-gray-500 mt-1 italic">
        Select a product above to see {label}.
      </p>
    );
  }
  if (!totalCount || filteredCount >= totalCount) return null;
  return (
    <p className="text-xs text-gray-500 mt-1">
      Showing {filteredCount} of {totalCount} {label} for the selected product
      {selectedLen !== 1 ? 's' : ''}.
    </p>
  );
}

/** Normalize stored keys to canonical names using current catalog (read / checkbox merge). */
function resolveProductLinkKeysToCanonical(keys, aliasMap, validNames) {
  const out = [];
  for (const k of keys || []) {
    const t = String(k).trim();
    if (!t) continue;
    const c = aliasMap.get(t) ?? (validNames.has(t) ? t : null);
    if (c != null && validNames.has(c) && !out.includes(c)) out.push(c);
  }
  return out;
}

/** Product rows: id/label for UI; productKey matches selectedProductKeys (playbook-level). */
const normalizeProducts = (items, tenantId) => {
  if (!items || !Array.isArray(items)) return [];
  return items
    .map((opt, index) => {
      if (typeof opt === 'string') {
        const productKey = normalizeProductAlignmentTag(opt);
        return {
          id: productKey || String(opt),
          label: getProductDisplayNameForTenant(tenantId, productKey, opt),
          productKey: productKey || String(opt),
        };
      }
      if (typeof opt === 'object' && opt !== null) {
        const rawKey = String(
          opt.product_key ?? opt.productKey ?? opt.id ?? opt.key ?? opt.value ?? `product_${index}`,
        );
        const productKey = normalizeProductAlignmentTag(rawKey);
        const id = String(opt.product_key ?? opt.id ?? opt.key ?? opt.value ?? `product_${index}`);
        const preferred = opt.product_name ?? opt.label ?? opt.name ?? opt.product_key ?? id;
        const label = getProductDisplayNameForTenant(tenantId, productKey, preferred);
        return {
          id,
          label,
          productKey: productKey === 'ALL' ? rawKey : productKey,
        };
      }
      const productKey = normalizeProductAlignmentTag(String(opt));
      return {
        id: String(opt),
        label: getProductDisplayNameForTenant(tenantId, productKey, String(opt)),
        productKey: productKey === 'ALL' ? String(opt) : productKey,
      };
    })
    .filter((p) => p.id && normalizeProductAlignmentTag(p.productKey) !== 'ALL');
};

/** Merge filter-options + personalization product rows; personalization names win over BE placeholders. */
function mergeDiyProductCatalog(primaryRows, personalizationRows, tenantId) {
  const byKey = new Map();
  const ingest = (rows, preferPersonalizationNames) => {
    for (const p of normalizeProducts(rows, tenantId)) {
      const pk = normalizeProductAlignmentTag(p.productKey ?? p.id);
      if (!pk) continue;
      const prev = byKey.get(pk);
      const candidate = preferPersonalizationNames
        ? p.label
        : prev?.label;
      const label = getProductDisplayNameForTenant(
        tenantId,
        pk,
        !isPlaceholderProductName(candidate) ? candidate : prev?.label,
      );
      byKey.set(pk, {
        id: String(p.id ?? pk),
        productKey: pk,
        label,
      });
    }
  };
  ingest(primaryRows, false);
  ingest(personalizationRows, true);
  const order =
    String(tenantId || '').toLowerCase() === 'cayuse'
      ? CAYUSE_KEYS
      : [...byKey.keys()].sort();
  const ordered = order.filter((k) => byKey.has(k)).map((k) => byKey.get(k));
  const rest = [...byKey.keys()]
    .filter((k) => !order.includes(k))
    .sort()
    .map((k) => byKey.get(k));
  return [...ordered, ...rest];
}

/** Rows from API for DIY educational-resource picker (preserves id/key for stable option values). */
function normalizeEducationalResourcesFromApi(rows, tenantId) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((raw) => {
      if (!raw || typeof raw !== 'object') return null;
      const title = String(raw.title ?? raw.name ?? '').trim();
      const name = String(raw.name ?? raw.title ?? '').trim();
      const url = String(raw.url ?? '').trim();
      if (!title && !name && !url) return null;
      const normalized = normalizeEducationalResourceRow(raw, tenantId);
      return {
        ...raw,
        title: title || name,
        name: name || title,
        url,
        product_context: normalized.product_context,
      };
    })
    .filter(Boolean);
}

function educationalResourceOptionValue(row, idx) {
  const id = row?.id != null && String(row.id).trim() !== '' ? String(row.id).trim() : '';
  const key = row?.key != null && String(row.key).trim() !== '' ? String(row.key).trim() : '';
  const title = String(row?.title ?? row?.name ?? '').trim();
  const url = String(row?.url ?? '').trim();
  return id || key || title || url || `edu_${idx}`;
}

function educationalResourceKeyMatchesRow(savedKey, row, idx) {
  const s = String(savedKey ?? '').trim();
  if (!s) return false;
  if (s === educationalResourceOptionValue(row, idx)) return true;
  const id = row?.id != null ? String(row.id).trim() : '';
  const key = row?.key != null ? String(row.key).trim() : '';
  const title = String(row?.title ?? row?.name ?? '').trim();
  const url = String(row?.url ?? '').trim();
  if (id && s === id) return true;
  if (key && s === key) return true;
  if (title && s === title) return true;
  if (url && s === url) return true;
  if (s === `edu_${idx}`) return true;
  return false;
}

function catalogRowMatchKey(row, kind) {
  if (row == null) return '';
  if (typeof row === 'string') return String(row).trim().toLowerCase();
  if (typeof row !== 'object') return '';
  if (kind === 'hook') {
    return String(row.hook ?? row.label ?? row.name ?? row.id ?? '').trim().toLowerCase();
  }
  if (kind === 'product_link') {
    return String(row.name ?? row.label ?? row.id ?? '').trim().toLowerCase();
  }
  return String(row.name ?? row.label ?? row.title ?? row.id ?? '').trim().toLowerCase();
}

/** Copy product_key from personalization rows onto filter-options rows matched by name/hook/title. */
function mergeProductKeysOntoCatalogRows(catalogRows, sourceRows, kind) {
  const byKey = new Map();
  for (const src of sourceRows || []) {
    if (!src || typeof src !== 'object') continue;
    const pkRaw = src.product_key ?? src.productKey ?? src.product_context;
    if (pkRaw == null || String(pkRaw).trim() === '') continue;
    const tag = normalizeProductAlignmentTag(pkRaw);
    const mk = catalogRowMatchKey(src, kind);
    if (mk) byKey.set(mk, tag);
  }
  return (catalogRows || []).map((row) => {
    if (typeof row === 'string') {
      const pk = byKey.get(String(row).trim().toLowerCase());
      if (!pk || pk === 'ALL') return { id: row, label: row };
      return { id: row, label: row, productKey: pk, product_key: pk };
    }
    if (!row || typeof row !== 'object') return row;
    const existing = row.product_key ?? row.productKey ?? row.product_context;
    if (existing != null && String(existing).trim() !== '') return row;
    const pk = byKey.get(catalogRowMatchKey(row, kind));
    if (!pk || pk === 'ALL') return row;
    return { ...row, productKey: pk, product_key: pk };
  });
}

/** When filter-options omits or fails to merge user KPIs/hooks (tenant mismatch, partial API), pull from settings/personalization. */
async function mergePersonalizationIntoFilterOptions(tenantId, userId, base) {
  const uid = userId != null ? String(userId).trim() : '';
  if (!uid || !tenantId) return base;
  const settings = base.settings || {
    kpis: [],
    hooks: [],
    caseStudies: [],
    productLinks: [],
    products: [],
    educationalResources: [],
  };
  const hasKpis = (settings.kpis || []).length > 0;
  const hasHooks = (settings.hooks || []).length > 0;
  const hasCase = (settings.caseStudies || []).length > 0;
  const hasPl = (settings.productLinks || []).length > 0;
  const hasProducts = (settings.products || []).length > 0;
  const hasCtas = (base.ctaStyles || []).length > 0;
  const eduRows = Array.isArray(settings.educationalResources) ? settings.educationalResources : [];
  const hasEdu = eduRows.length > 0;
  if (hasKpis && hasHooks && hasCase && hasPl && hasProducts && hasCtas && hasEdu) return base;
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/settings/personalization?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(uid)}`,
      { credentials: 'include' }
    );
    if (!res.ok) return base;
    const data = await res.json();
    const mergedEdu = hasEdu
      ? eduRows.map((row) => {
          const n = normalizeEducationalResourceRow(row, tenantId);
          return { ...row, product_context: n.product_context };
        })
      : normalizeEducationalResourcesFromApi(
          data.educational_resources ?? data.settings?.educational_resources ?? [],
          tenantId,
        );
    const rawHooks = data.hooks ?? data.messaging_hooks ?? [];
    const kpis = mergeProductKeysOntoCatalogRows(
      hasKpis ? settings.kpis : normalizeOptions(data.kpis),
      data.kpis,
      'kpi',
    );
    const hooks = mergeProductKeysOntoCatalogRows(
      hasHooks ? settings.hooks : normalizeOptions(rawHooks),
      rawHooks,
      'hook',
    );
    const caseStudies = mergeProductKeysOntoCatalogRows(
      hasCase ? settings.caseStudies : normalizeOptions(data.case_study_links),
      data.case_study_links,
      'case_study',
    );
    const productLinks = mergeProductKeysOntoCatalogRows(
      hasPl ? settings.productLinks : normalizeOptions(data.product_links, true),
      data.product_links,
      'product_link',
    );

    const mergedProducts = mergeDiyProductCatalog(
      hasProducts ? settings.products : data.settings?.products,
      data.products,
      tenantId,
    );

    return {
      ...base,
      settings: {
        kpis,
        hooks,
        caseStudies,
        productLinks,
        products:
          mergedProducts.length > 0
            ? mergedProducts
            : hasProducts
              ? normalizeProducts(settings.products, tenantId)
              : normalizeProducts(data.products, tenantId),
        educationalResources: mergedEdu,
      },
      ctaStyles: hasCtas ? base.ctaStyles : normalizeOptions(data.cta_styles || data.ctaStyles),
    };
  } catch {
    return base;
  }
}

function normalizeDiyAccountId(raw) {
  if (raw == null || raw === '') return null;
  return String(raw).trim();
}

/** Add both `id` and `account_id` from an account row so personas match whichever field the API uses. */
function addAccountRowIdsToSet(accountRow, set) {
  if (!accountRow || typeof set?.add !== 'function') return;
  const i1 = normalizeDiyAccountId(accountRow.id);
  const i2 = normalizeDiyAccountId(accountRow.account_id);
  const i3 = normalizeDiyAccountId(accountRow.global_pool_account_id);
  if (i1) set.add(i1);
  if (i2) set.add(i2);
  if (i3) set.add(i3);
}

function findAccountRowByAnyId(accounts, rawId) {
  const n = normalizeDiyAccountId(rawId);
  if (!n || !Array.isArray(accounts)) return null;
  return (
    accounts.find(
      (a) =>
        normalizeDiyAccountId(a?.id) === n ||
        normalizeDiyAccountId(a?.account_id) === n ||
        normalizeDiyAccountId(a?.global_pool_account_id) === n
    ) || null
  );
}

/** Account IDs that DIY persona pickers should respect (aligned with Account Selection above). */
function buildPersonaEligibleAccountIdSet(formValues, accounts) {
  const set = new Set();
  const mode = formValues.accountSelectionMode;
  const accList = Array.isArray(accounts) ? accounts : [];

  if (mode === 'specific_accounts') {
    for (const raw of formValues.selectedAccountIds || []) {
      const n = normalizeDiyAccountId(raw);
      if (!n) continue;
      set.add(n);
      const row = findAccountRowByAnyId(accList, n);
      if (row) addAccountRowIdsToSet(row, set);
    }
    return set;
  }
  if (mode === 'by_fit_score') {
    const min = formValues.minAccountFitScore;
    if (min === undefined || min === null) return set;
    const minN = Number(min);
    for (const a of accList) {
      const score = Number(a.fitScore ?? a.account_fit_score ?? 0);
      if (score >= minN) addAccountRowIdsToSet(a, set);
    }
    return set;
  }
  for (const a of accList) {
    addAccountRowIdsToSet(a, set);
  }
  return set;
}

function personaBelongsToEligibleAccounts(persona, eligibleSet, accounts) {
  if (!persona || eligibleSet.size === 0) return false;

  const personaAccountIds = new Set();
  for (const key of ['account_id', 'accountId', 'account_final_id', 'company_id']) {
    const v = normalizeDiyAccountId(persona[key]);
    if (v) personaAccountIds.add(v);
  }
  for (const pid of personaAccountIds) {
    if (eligibleSet.has(pid)) return true;
  }

  const nameLower = (persona.account_name || '').trim().toLowerCase();
  if (nameLower && Array.isArray(accounts)) {
    for (const a of accounts) {
      const an = (a.name || a.account_name || '').trim().toLowerCase();
      if (!an || an !== nameLower) continue;
      const id1 = normalizeDiyAccountId(a.id);
      const id2 = normalizeDiyAccountId(a.account_id);
      const id3 = normalizeDiyAccountId(a.global_pool_account_id);
      if ((id1 && eligibleSet.has(id1)) || (id2 && eligibleSet.has(id2)) || (id3 && eligibleSet.has(id3))) return true;
    }
  }
  return false;
}

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';

const CHANNEL_ICONS = {
  email: Mail,
  linkedin: Linkedin,
  call: Phone
};

const STANDARD_ARCHETYPES = ['Economic Buyer', 'Champion', 'Influencer', 'Evaluator', 'End User'];

const DEFAULT_STEP = () => ({
  id: crypto.randomUUID(),
  order: 1,
  channel: 'email',
  dayOffset: 0,
  strategicHook: '',
  messageFocus: '',
  kpiKey: null,
  hookKey: null,
  caseStudyKey: null,
  productLinkKeys: [],
  educationalResourceKeys: [],
  ctaStyleKey: null,
});

const defaultFormValues = {
  name: '',
  goalDescription: '',
  countryMode: 'all',
  countries: [],
  sectorMode: 'all',
  sectors: [],
  accountSelectionMode: 'all_in_sector',
  selectedAccountIds: [],
  minAccountFitScore: undefined,
  personaAudienceMode: 'all_matching',
  selectedPersonaIds: [],
  messagingLanguageMode: 'auto_by_country',
  includeAllArchetypes: true,
  archetypes: [],
  includeAllDepartments: true,
  departments: [],
  roleTitleKeywords: [],
  useSignals: true,
  enabledChannels: ['email'],
  selectedProductKeys: [],
  steps: [DEFAULT_STEP()],
  autoScheduleEmails: false
};

export default function DIYPlaybookForm({
  initialValues,
  tenantId,
  userId,
  onSubmit,
  onCancel,
  isLoading = false,
  isSaving = false
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const contextUser = useUser();
  const effectiveUserId = String(
    userId ?? contextUser?.id ?? contextUser?.user_id ?? '',
  ).trim();
  const isReadOnlyPlaybook = useMemo(() => {
    const owner = initialValues?.owner_user_id != null
      ? String(initialValues.owner_user_id).trim()
      : '';
    if (!owner || !effectiveUserId) return false;
    return owner !== effectiveUserId;
  }, [initialValues?.owner_user_id, effectiveUserId]);
  const [formValues, setFormValues] = useState({ ...defaultFormValues });
  const [filterOptions, setFilterOptions] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [availablePersonasForManual, setAvailablePersonasForManual] = useState([]);
  const [isLoadingManualPersonas, setIsLoadingManualPersonas] = useState(false);
  const [manualPersonaSearchTerm, setManualPersonaSearchTerm] = useState('');
  const [pursuitPersonas, setPursuitPersonas] = useState([]);
  const [isLoadingPursuitPersonas, setIsLoadingPursuitPersonas] = useState(false);
  const [pursuitPersonaSearchTerm, setPursuitPersonaSearchTerm] = useState('');
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [debouncedAccountSearch, setDebouncedAccountSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedAccountSearch(accountSearchTerm.trim()), 350);
    return () => clearTimeout(t);
  }, [accountSearchTerm]);
  const [isCloning, setIsCloning] = useState(false);
  const assetCatalogAuditPlaybookIdRef = useRef(null);

  // Update form values when initialValues prop changes (e.g., when editing existing playbook)
  useEffect(() => {
    if (initialValues && !hasInitialized) {
      console.log('📝 DIYPlaybookForm: Loading initial values:', initialValues);
      console.log('📝 Steps from initialValues:', initialValues.steps);  // ADD THIS
      setFormValues({
        ...defaultFormValues,
        ...initialValues,
        // Ensure arrays are always arrays
        countries: Array.isArray(initialValues.countries) ? initialValues.countries : [],
        sectors: Array.isArray(initialValues.sectors) ? initialValues.sectors : [],
        archetypes: Array.isArray(initialValues.archetypes) ? initialValues.archetypes : [],
        departments: Array.isArray(initialValues.departments) ? initialValues.departments : [],
        roleTitleKeywords: Array.isArray(initialValues.roleTitleKeywords) ? initialValues.roleTitleKeywords : [],
        selectedAccountIds: Array.isArray(initialValues.selectedAccountIds)
          ? initialValues.selectedAccountIds
              .map((id) => normalizeDiyAccountId(id))
              .filter(Boolean)
          : [],
        selectedProductKeys: Array.isArray(initialValues.selectedProductKeys)
          ? initialValues.selectedProductKeys
          : [],
        personaAudienceMode: initialValues.personaAudienceMode || 'all_matching',
        selectedPersonaIds: Array.isArray(initialValues.selectedPersonaIds) ? initialValues.selectedPersonaIds : [],
        messagingLanguageMode:
          initialValues.messagingLanguageMode === 'english_only' ||
          initialValues.messagingLanguageMode === 'auto_by_country'
            ? initialValues.messagingLanguageMode
            : 'auto_by_country',
        enabledChannels: Array.isArray(initialValues.enabledChannels) && initialValues.enabledChannels.length > 0 
          ? initialValues.enabledChannels 
          : ['email'],
        steps:
          Array.isArray(initialValues.steps) && initialValues.steps.length > 0
            ? initialValues.steps.map((s, i) => {
                const base = DEFAULT_STEP();
                const {
                  product_context: _pcSnake,
                  productContext: _pcCamel,
                  product_keys: _pkSnake,
                  productKeys: _pkCamel,
                  ...sRest
                } = s || {};
                return {
                  ...base,
                  ...sRest,
                  id: s.id || base.id,
                  order: s.order ?? i + 1,
                  channel: resolveDiyStepChannelFromApiStep(s),
                  productLinkKeys: Array.isArray(s.productLinkKeys) ? s.productLinkKeys : [],
                  educationalResourceKeys: Array.isArray(s.educationalResourceKeys)
                    ? s.educationalResourceKeys
                    : Array.isArray(s.educational_resource_keys)
                      ? s.educational_resource_keys
                      : [],
                };
              })
            : [DEFAULT_STEP()],
      });
      setHasInitialized(true);
    }
  }, [initialValues, hasInitialized]);

  const linkedInStepsDebugKey = useMemo(
    () => JSON.stringify((formValues.steps || []).map((s) => s?.channel)),
    [formValues.steps],
  );

  useEffect(() => {
    if (!hasInitialized) return;
    (formValues.steps || []).forEach((step, idx) => {
      const ch = String(step?.channel || '').toLowerCase();
      if (!ch.includes('linkedin')) return;
      logLinkedInChannelResolution(`DIYPlaybookForm:step:${idx + 1}`, {
        ...step,
        channel: resolveDiyStepLinkedInChannel(step.channel),
        allSteps: formValues.steps,
        stepIndex: idx,
      });
    });
  }, [linkedInStepsDebugKey, hasInitialized, formValues.steps]);

  const [accounts, setAccounts] = useState([]);
  const [accountsListMeta, setAccountsListMeta] = useState({ returned: 0, total: 0, truncated: false });
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [keywordInput, setKeywordInput] = useState('');
  const [expandedSteps, setExpandedSteps] = useState({});
  const [showAssetStripBanner, setShowAssetStripBanner] = useState(false);

  const personaEligibleAccountIdSet = useMemo(
    () => buildPersonaEligibleAccountIdSet(formValues, accounts),
    [
      formValues.accountSelectionMode,
      formValues.selectedAccountIds,
      formValues.minAccountFitScore,
      accounts,
    ]
  );

  const selectedAccountIdSet = useMemo(
    () =>
      new Set(
        (formValues.selectedAccountIds || [])
          .map((id) => normalizeDiyAccountId(id))
          .filter(Boolean)
      ),
    [formValues.selectedAccountIds]
  );

  const marketFitBounds = useMemo(() => fitScoreBounds(accounts), [accounts]);

  const audienceEmptyReason = useMemo(
    () =>
      audiencePreview
        ? previewEmptyReason({
            accountSelectionMode: formValues.accountSelectionMode,
            minAccountFitScore: formValues.minAccountFitScore,
            personaAudienceMode: formValues.personaAudienceMode,
            selectedPersonaIds: formValues.selectedPersonaIds,
            accountsCount: audiencePreview.accountsCount,
            fitBounds: marketFitBounds,
          })
        : null,
    [
      audiencePreview,
      formValues.accountSelectionMode,
      formValues.minAccountFitScore,
      formValues.personaAudienceMode,
      formValues.selectedPersonaIds,
      marketFitBounds,
    ],
  );

  const filteredAccounts = useMemo(() => {
    const q = accountSearchTerm.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((account) => {
      const name = String(account.name || account.account_name || '').toLowerCase();
      const country = String(account.country || '').toLowerCase();
      return name.includes(q) || country.includes(q);
    });
  }, [accounts, accountSearchTerm]);

  const diyAlignedAssets = useMemo(() => {
    const settings = filterOptions?.settings;
    if (!settings) return null;
    const selected = formValues.selectedProductKeys || [];
    const catalogProducts = settings.products || [];
    const edu = normalizeEducationalResourcesFromApi(
      settings.educationalResources || [],
      tenantId
    );

    if (catalogProducts.length === 0) {
      return {
        kpis: settings.kpis || [],
        hooks: settings.hooks || [],
        caseStudies: settings.caseStudies || [],
        productLinks: settings.productLinks || [],
        educationalResources: edu,
      };
    }

    const aligned = {
      kpis: filterAssetsForDiyProductAlignment(settings.kpis, selected, catalogProducts),
      hooks: filterAssetsForDiyProductAlignment(settings.hooks, selected, catalogProducts),
      caseStudies: filterAssetsForDiyProductAlignment(
        settings.caseStudies,
        selected,
        catalogProducts,
      ),
      productLinks: filterAssetsForDiyProductAlignment(
        settings.productLinks,
        selected,
        catalogProducts,
      ),
      educationalResources: filterAssetsForDiyProductAlignment(
        edu,
        selected,
        catalogProducts,
      ),
    };
    if (isDiyProductAlignmentDebugEnabled() && selected.length > 0) {
      const resolved = resolveSelectedProductKeysForDiyAlignment(selected, catalogProducts);
      console.log('[diy_product_alignment]', {
        selected,
        resolved,
        counts: {
          kpis: `${aligned.kpis.length}/${(settings.kpis || []).length}`,
          hooks: `${aligned.hooks.length}/${(settings.hooks || []).length}`,
          caseStudies: `${aligned.caseStudies.length}/${(settings.caseStudies || []).length}`,
          productLinks: `${aligned.productLinks.length}/${(settings.productLinks || []).length}`,
          educationalResources: `${aligned.educationalResources.length}/${edu.length}`,
        },
        sampleTags: (settings.kpis || []).slice(0, 3).map((k) => ({
          label: k?.label ?? k?.name,
          product_key: k?.product_key ?? k?.productKey,
        })),
      });
    }
    return aligned;
  }, [
    filterOptions?.settings,
    formValues.selectedProductKeys,
    tenantId,
  ]);

  const tenantHasProducts = (filterOptions?.settings?.products || []).length > 0;
  const productGateBlocks =
    tenantHasProducts && !hasDiyProductSelection(formValues.selectedProductKeys);

  const validProductLinkNames = useMemo(
    () =>
      new Set(
        (diyAlignedAssets?.productLinks || [])
          .map((p) => canonicalProductLinkName(p))
          .filter(Boolean)
      ),
    [diyAlignedAssets?.productLinks]
  );

  const productLinkAliasToCanonical = useMemo(
    () => buildProductLinkAliasToCanonicalMap(diyAlignedAssets?.productLinks || []),
    [diyAlignedAssets?.productLinks]
  );

  const educationalResourcesForPicker = useMemo(
    () => diyAlignedAssets?.educationalResources ?? [],
    [diyAlignedAssets?.educationalResources]
  );

  const catalogEducationalResourceCount = useMemo(() => {
    const settings = filterOptions?.settings;
    if (!settings) return 0;
    return normalizeEducationalResourcesFromApi(
      settings.educationalResources || [],
      tenantId
    ).length;
  }, [filterOptions?.settings, tenantId]);

  // Load filter options on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      setIsLoadingOptions(true);
      try {
        const tenantQ = encodeURIComponent(tenantId);
        const userQ = userId != null && String(userId).trim() !== ''
          ? `&user_id=${encodeURIComponent(String(userId).trim())}`
          : '';
        const response = await fetch(
          `${API_BASE_URL}/api/diy-playbooks/filter-options?tenant_id=${tenantQ}${userQ}`,
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          console.log('✅ API filter-options response:', data);
          console.log('✅ Departments from API:', data.departments);
          let next = {
            countries: normalizeCountries(data.countries),
            sectors: normalizeSectors(data.sectors),
            archetypes: Array.isArray(data.archetypes) ? data.archetypes.filter(a => typeof a === 'string') : [],
            departments: Array.isArray(data.departments) ? data.departments.filter(d => typeof d === 'string') : [],
            settings: {
              kpis: normalizeOptions(data.settings?.kpis),
              hooks: normalizeOptions(data.settings?.hooks),
              caseStudies: normalizeOptions(data.settings?.caseStudies),
              productLinks: normalizeOptions(data.settings?.productLinks, true),
              products: normalizeProducts(data.settings?.products, tenantId),
              educationalResources: normalizeEducationalResourcesFromApi(
                data.settings?.educational_resources ??
                  data.educational_resources ??
                  data.settings?.educationalResources ??
                  [],
                tenantId
              ),
            },
            ctaStyles: normalizeOptions(data.ctaStyles),
          };
          next = await mergePersonalizationIntoFilterOptions(tenantId, userId, next);
          setFilterOptions(next);
        } else {
          // Fallback: Load sectors and countries from AccountFinal entity
          const { AccountFinal } = await import('@/api/entities');
          const allAccountsData = await AccountFinal.list();
          // Filter to include accounts owned by tenant OR visible to tenant
          const accountsData = allAccountsData.filter(a =>
            (a.tenant_id === tenantId ||
            (Array.isArray(a.visible_to_tenants) && a.visible_to_tenants.includes(tenantId))) &&
            a.is_visible !== false &&
            !isAccountExcludedFromPlaybooks(tenantId, a)
          );
          const rawSectors = [...new Set(accountsData.map(a => a.sector).filter(Boolean))].sort();
          const rawCountries = [...new Set(accountsData.map(a => a.country).filter(Boolean))].sort();
          const departments = [...new Set(accountsData.flatMap(a => a.departments || []).filter(Boolean))].sort();
          let next = {
            countries: normalizeCountries(rawCountries),
            sectors: normalizeSectors(rawSectors),
            departments,
            archetypes: [],
            settings: {
              kpis: [],
              hooks: [],
              caseStudies: [],
              productLinks: [],
              products: [],
              educationalResources: [],
            },
            ctaStyles: [],
          };
          next = await mergePersonalizationIntoFilterOptions(tenantId, userId, next);
          setFilterOptions(next);
        }
      } catch (error) {
        console.error('Error loading filter options:', error);
        // Fallback on error too
        try {
          const { AccountFinal } = await import('@/api/entities');
          const allAccountsData = await AccountFinal.list();
          // Filter to include accounts owned by tenant OR visible to tenant
          const accountsData = allAccountsData.filter(a =>
            (a.tenant_id === tenantId ||
            (Array.isArray(a.visible_to_tenants) && a.visible_to_tenants.includes(tenantId))) &&
            a.is_visible !== false &&
            !isAccountExcludedFromPlaybooks(tenantId, a)
          );
          const rawSectors = [...new Set(accountsData.map(a => a.sector).filter(Boolean))].sort();
          const rawCountries = [...new Set(accountsData.map(a => a.country).filter(Boolean))].sort();
          let next = {
            countries: normalizeCountries(rawCountries),
            sectors: normalizeSectors(rawSectors),
            departments: [],
            archetypes: [],
            settings: {
              kpis: [],
              hooks: [],
              caseStudies: [],
              productLinks: [],
              products: [],
              educationalResources: [],
            },
            ctaStyles: [],
          };
          next = await mergePersonalizationIntoFilterOptions(tenantId, userId, next);
          setFilterOptions(next);
        } catch (e) {
          console.error('Fallback also failed:', e);
        }
      } finally {
        setIsLoadingOptions(false);
      }
    };
    loadFilterOptions();
  }, [tenantId, userId]);

  // Strip per-step asset keys that are not valid for the current owner catalog (clone/API load + FE catalog).
  useEffect(() => {
    if (!filterOptions?.settings || isLoadingOptions) return;
    if (initialValues && !hasInitialized) return;

    setFormValues((prev) => {
      // Sanitize against the FULL settings catalog, not the product-filtered
      // visibility set. Product alignment only controls what pickers SHOW.
      const settingsForCatalog = filterOptions.settings;
      const catalog = buildDiyStepAssetCatalogFromFormSettings(settingsForCatalog);
      if (!catalog) return prev;
      const { steps: nextSteps, changed } = sanitizeDiyStepsArray(prev.steps, catalog, {
        logWarnings: true,
      });
      if (!changed) return prev;
      requestAnimationFrame(() => setShowAssetStripBanner(true));
      return { ...prev, steps: nextSteps };
    });
  }, [
    filterOptions,
    isLoadingOptions,
    hasInitialized,
    initialValues,
  ]);

  // Once per playbook load: log whether step asset refs exist in owner filter-options catalog.
  useEffect(() => {
    if (!filterOptions?.settings || isLoadingOptions || !hasInitialized) return;
    const playbookId = initialValues?.id != null ? String(initialValues.id).trim() : '';
    if (!playbookId || assetCatalogAuditPlaybookIdRef.current === playbookId) return;
    assetCatalogAuditPlaybookIdRef.current = playbookId;

    const audit = auditDiyPlaybookStepAssets(formValues.steps, filterOptions.settings);
    if (!audit) return;

    const selected = formValues.selectedProductKeys || [];
    const catalogProducts = filterOptions.settings.products || [];
    const aligned = {
      kpis: filterAssetsForDiyProductAlignment(
        filterOptions.settings.kpis,
        selected,
        catalogProducts,
      ),
      hooks: filterAssetsForDiyProductAlignment(
        filterOptions.settings.hooks,
        selected,
        catalogProducts,
      ),
      productLinks: filterAssetsForDiyProductAlignment(
        filterOptions.settings.productLinks,
        selected,
        catalogProducts,
      ),
    };

    logDiyPlaybookClone('asset_catalog_audit', {
      playbookId,
      playbookName: formValues.name ?? initialValues?.name ?? null,
      ownerUserId: initialValues?.owner_user_id ?? null,
      selectedProductKeys: selected,
      ...audit,
      pickerVisibleForSelection: {
        kpis: aligned.kpis.length,
        hooks: aligned.hooks.length,
        productLinks: aligned.productLinks.length,
      },
      note:
        'catalogCounts = your filter-options/personalization library; allStepAssetsResolvable = step keys still valid; pickerVisible = assets shown after product selection',
    });
  }, [
    filterOptions,
    isLoadingOptions,
    hasInitialized,
    initialValues?.id,
    initialValues?.name,
    initialValues?.owner_user_id,
    formValues.steps,
    formValues.name,
    formValues.selectedProductKeys,
  ]);

  // Load accounts when sector or country changes
  useEffect(() => {
    if (!filterOptions?.sectors) return;

    const loadAccounts = async () => {
      setIsLoadingAccounts(true);
      try {
        let url = `${API_BASE_URL}/api/diy-playbooks/accounts?tenant_id=${tenantId}`;
        
        // Add country param if in individual mode with selected countries
        if (formValues.countryMode === 'individual' && formValues.countries?.length > 0) {
          url += `&country=${encodeURIComponent(formValues.countries.join(','))}`;
        }
        
        // Only add sector param if in individual mode with selected sectors
        if (formValues.sectorMode === 'individual' && formValues.sectors?.length > 0) {
          url += `&sector=${encodeURIComponent(formValues.sectors.join(','))}`;
        }
        if (formValues.accountSelectionMode === 'specific_accounts' && debouncedAccountSearch.length >= 2) {
          url += `&q=${encodeURIComponent(debouncedAccountSearch)}`;
        }
        // For 'all' mode, don't pass sector param - API will return all accounts
        
        const response = await fetch(url, { credentials: 'include' });
        
        if (response.ok) {
          const data = await response.json();
          const accountsList = (data.accounts || []).filter(a =>
            a.is_visible !== false && !isAccountExcludedFromPlaybooks(tenantId, a)
          );
          setAccounts(accountsList);
          setAccountsListMeta(diyAccountsListTruncation(data, accountsList.length));
        } else {
          console.warn('API returned error, using fallback');
          // Fallback: Load from AccountFinal entity
          try {
            const { AccountFinal } = await import('@/api/entities');
            const allAccounts = await AccountFinal.list();
            // Filter to include accounts owned by tenant OR visible to tenant
            let filtered = allAccounts.filter(a =>
              (a.tenant_id === tenantId || (Array.isArray(a.visible_to_tenants) && a.visible_to_tenants.includes(tenantId))) &&
              a.is_visible !== false &&
              !isAccountExcludedFromPlaybooks(tenantId, a)
            );
            
            // Apply country filter
            if (formValues.countryMode === 'individual' && formValues.countries?.length > 0) {
              filtered = filtered.filter(a => formValues.countries.includes(a.country));
            }
            
            // Apply sector filter
            if (formValues.sectorMode === 'individual' && formValues.sectors?.length > 0) {
              filtered = filtered.filter(a => formValues.sectors.includes(a.sector));
            }
            
            setAccounts(filtered || []);
            setAccountsListMeta(diyAccountsListTruncation({}, (filtered || []).length));
          } catch (fallbackError) {
            console.error('Fallback failed:', fallbackError);
            setAccounts([]);
            setAccountsListMeta({ returned: 0, total: 0, truncated: false });
          }
        }
      } catch (error) {
        console.error('Error loading accounts:', error);
        // Fallback on network error
        try {
          const { AccountFinal } = await import('@/api/entities');
          const allAccounts = await AccountFinal.list();
          if (allAccounts) {
            // Filter to include accounts owned by tenant OR visible to tenant
            let filtered = allAccounts.filter(a =>
              (a.tenant_id === tenantId || (Array.isArray(a.visible_to_tenants) && a.visible_to_tenants.includes(tenantId))) &&
              a.is_visible !== false &&
              !isAccountExcludedFromPlaybooks(tenantId, a)
            );
            
            // Apply country filter
            if (formValues.countryMode === 'individual' && formValues.countries?.length > 0) {
              filtered = filtered.filter(a => formValues.countries.includes(a.country));
            }
            
            // Apply sector filter
            if (formValues.sectorMode === 'individual' && formValues.sectors?.length > 0) {
              filtered = filtered.filter(a => formValues.sectors.includes(a.sector));
            }
            
            setAccounts(filtered || []);
            setAccountsListMeta(diyAccountsListTruncation({}, (filtered || []).length));
          } else {
            setAccounts([]);
            setAccountsListMeta({ returned: 0, total: 0, truncated: false });
          }
        } catch (e) {
          console.error('Fallback also failed:', e);
          setAccounts([]);
          setAccountsListMeta({ returned: 0, total: 0, truncated: false });
        }
      } finally {
        setIsLoadingAccounts(false);
      }
    };
    
    loadAccounts();
  }, [formValues.countryMode, formValues.countries, formValues.sectorMode, formValues.sectors, formValues.accountSelectionMode, debouncedAccountSearch, tenantId, filterOptions?.sectors]);

  // Fetch ALL personas for manual selection when mode changes
  useEffect(() => {
    const fetchPersonasForManualSelection = async () => {
      // Only fetch when manual mode is selected
      if (formValues.personaAudienceMode !== 'manual') {
        setAvailablePersonasForManual([]);
        return;
      }
      
      setIsLoadingManualPersonas(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/diy-playbooks/all-personas?tenant_id=${tenantId}`,
          { credentials: 'include' }
        );
        
        if (response.ok) {
          const data = await response.json();
          const personas = (data.personas || data || []).filter((p) => p?.is_visible !== false);
          setAvailablePersonasForManual(personas);
          console.log('📋 Loaded ALL personas for manual selection:', personas.length);
        } else {
          console.error('Failed to fetch personas for manual selection');
          setAvailablePersonasForManual([]);
        }
      } catch (error) {
        console.error('Error fetching personas for manual selection:', error);
        setAvailablePersonasForManual([]);
      } finally {
        setIsLoadingManualPersonas(false);
      }
    };
    
    fetchPersonasForManualSelection();
  }, [formValues.personaAudienceMode, tenantId]);

  // Fetch pursuit personas when pursuit_only mode is selected
  useEffect(() => {
    const fetchPursuitPersonas = async () => {
      if (formValues.personaAudienceMode !== 'pursuit_only') {
        setPursuitPersonas([]);
        return;
      }
      
      setIsLoadingPursuitPersonas(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/diy-playbooks/personas-in-pursuit?tenant_id=${tenantId}&user_id=${userId}`,
          { credentials: 'include' }
        );
        
        if (response.ok) {
          const data = await response.json();
          const personas = (data.personas || data || []).filter((p) => p?.is_visible !== false);
          setPursuitPersonas(personas);
          console.log('⭐ Loaded pursuit personas:', personas.length);
        } else {
          console.error('Failed to fetch pursuit personas');
          setPursuitPersonas([]);
        }
      } catch (error) {
        console.error('Error fetching pursuit personas:', error);
        setPursuitPersonas([]);
      } finally {
        setIsLoadingPursuitPersonas(false);
      }
    };
    
    fetchPursuitPersonas();
  }, [formValues.personaAudienceMode, tenantId, userId]);

  const updateFormValue = (key, value) => {
    setFormValues(prev => ({ ...prev, [key]: value }));
    // Clear validation error for this field
    if (validationErrors[key]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const accountScopedManualPersonas = useMemo(
    () =>
      availablePersonasForManual.filter((p) =>
        personaBelongsToEligibleAccounts(p, personaEligibleAccountIdSet, accounts)
      ),
    [availablePersonasForManual, personaEligibleAccountIdSet, accounts]
  );

  const filteredManualPersonas = useMemo(() => {
    if (!manualPersonaSearchTerm) return accountScopedManualPersonas;
    const searchLower = manualPersonaSearchTerm.toLowerCase();
    return accountScopedManualPersonas.filter(
      (p) =>
        p.name?.toLowerCase().includes(searchLower) ||
        p.first_name?.toLowerCase().includes(searchLower) ||
        p.last_name?.toLowerCase().includes(searchLower) ||
        p.account_name?.toLowerCase().includes(searchLower) ||
        p.role_title?.toLowerCase().includes(searchLower)
    );
  }, [accountScopedManualPersonas, manualPersonaSearchTerm]);

  const accountScopedPursuitPersonas = useMemo(
    () =>
      pursuitPersonas.filter((p) =>
        personaBelongsToEligibleAccounts(p, personaEligibleAccountIdSet, accounts)
      ),
    [pursuitPersonas, personaEligibleAccountIdSet, accounts]
  );

  const filteredPursuitPersonas = useMemo(() => {
    if (!pursuitPersonaSearchTerm) return accountScopedPursuitPersonas;
    const searchLower = pursuitPersonaSearchTerm.toLowerCase();
    return accountScopedPursuitPersonas.filter(
      (p) =>
        p.name?.toLowerCase().includes(searchLower) ||
        p.first_name?.toLowerCase().includes(searchLower) ||
        p.last_name?.toLowerCase().includes(searchLower) ||
        p.account_name?.toLowerCase().includes(searchLower) ||
        p.role_title?.toLowerCase().includes(searchLower)
    );
  }, [accountScopedPursuitPersonas, pursuitPersonaSearchTerm]);

  /** Drop persona selections that fall outside the current account scope (manual / pursuit). */
  useEffect(() => {
    setFormValues((prev) => {
      const mode = prev.personaAudienceMode;
      if (mode !== 'manual' && mode !== 'pursuit_only') return prev;
      const ids = prev.selectedPersonaIds || [];
      if (ids.length === 0) return prev;
      const pool = mode === 'manual' ? availablePersonasForManual : pursuitPersonas;
      if (pool.length === 0) return prev;
      if (mode === 'manual' && availablePersonasForManual.length === 0) return prev;
      if (mode === 'pursuit_only' && pursuitPersonas.length === 0) return prev;
      if (accounts.length === 0) return prev;
      const eligibleSet = buildPersonaEligibleAccountIdSet(prev, accounts);
      console.log('🧪 persona scope reconciliation', {
        mode,
        ids,
        eligibleAccountIds: Array.from(eligibleSet),
        poolSample: pool.slice(0, 5),
      });
      const next = ids.filter((pid) => {
        const p = pool.find((x) => x.person_id === pid);
        if (!p) return true;
        return personaBelongsToEligibleAccounts(p, eligibleSet, accounts);
      });
      if (next.length === ids.length) return prev;
      return { ...prev, selectedPersonaIds: next };
    });
  }, [
    accounts,
    availablePersonasForManual,
    pursuitPersonas,
    personaEligibleAccountIdSet,
    formValues.personaAudienceMode,
    formValues.accountSelectionMode,
    formValues.selectedAccountIds,
    formValues.minAccountFitScore,
  ]);

  /**
   * Pursuit: when nothing is selected, default to all eligible pursuit personas (scoped to accounts above).
   * Omits selectedPersonaIds from deps so clearing the selection stays cleared until account/pursuit data changes.
   */
  useEffect(() => {
    if (formValues.personaAudienceMode !== 'pursuit_only') return;
    if ((formValues.selectedPersonaIds || []).length > 0) return;
    const eligiblePers = pursuitPersonas.filter((p) =>
      personaBelongsToEligibleAccounts(p, personaEligibleAccountIdSet, accounts)
    );
    if (eligiblePers.length === 0) return;
    updateFormValue('selectedPersonaIds', eligiblePers.map((p) => p.person_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- do not re-run when user clears selection
  }, [formValues.personaAudienceMode, pursuitPersonas, personaEligibleAccountIdSet, accounts]);

  const updateStep = (stepId, key, value) => {
    setFormValues((prev) => {
      let steps = prev.steps.map((step) =>
        step.id === stepId ? { ...step, [key]: value } : step,
      );
      if (key === 'channel') {
        steps = enforceLinkedInChannelSequence(steps);
      }
      return { ...prev, steps };
    });
  };

  const addStep = () => {
    const lastStep = formValues.steps[formValues.steps.length - 1];
    const base = DEFAULT_STEP();
    const newStep = {
      ...base,
      order: formValues.steps.length + 1,
      channel: formValues.enabledChannels[0] || 'email',
      dayOffset: (lastStep?.dayOffset || 0) + 3,
      strategicHook: '',
      messageFocus: '',
    };
    setFormValues((prev) => ({
      ...prev,
      steps: enforceLinkedInChannelSequence([...prev.steps, newStep]),
    }));
  };

  const removeStep = (stepId) => {
    if (formValues.steps.length <= 1) return;
    setFormValues((prev) => ({
      ...prev,
      steps: enforceLinkedInChannelSequence(
        prev.steps
          .filter((step) => step.id !== stepId)
          .map((step, idx) => ({ ...step, order: idx + 1 })),
      ),
    }));
  };

  const addKeyword = (e) => {
    if (e.key === 'Enter' && keywordInput.trim()) {
      e.preventDefault();
      if (!formValues.roleTitleKeywords.includes(keywordInput.trim())) {
        updateFormValue('roleTitleKeywords', [...formValues.roleTitleKeywords, keywordInput.trim()]);
      }
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    updateFormValue('roleTitleKeywords', formValues.roleTitleKeywords.filter(k => k !== keyword));
  };

  const handlePreviewAudience = async () => {
    setIsLoadingPreview(true);
    try {
      console.log('🔎 Sending to API - roleTitleKeywords:', formValues.roleTitleKeywords);
      const previewHeaders = { 'Content-Type': 'application/json' };
      if (effectiveUserId) previewHeaders['X-User-ID'] = effectiveUserId;

      const response = await fetch(`${API_BASE_URL}/api/diy-playbooks/preview-targeting`, {
        method: 'POST',
        headers: previewHeaders,
        credentials: 'include',
        body: JSON.stringify(
          buildDiyPreviewPayload(formValues, {
            tenantId,
            userId: effectiveUserId || undefined,
          }),
        ),
      });
      
      if (response.ok) {
        const data = await response.json();
        setAudiencePreview({
          accountsCount: data.accounts_count || data.accountsCount || 0,
          personasCount: data.personas_count || data.personasCount || 0
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Preview failed:', errorData);
        setAudiencePreview({ accountsCount: 0, personasCount: 0 });
      }
    } catch (error) {
      console.error('Error previewing audience:', error);
      setAudiencePreview({ accountsCount: 0, personasCount: 0 });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handlePreviewAudienceRef = useRef(handlePreviewAudience);
  handlePreviewAudienceRef.current = handlePreviewAudience;

  // Keep "All matching personas" audience counts in sync with Account Selection (and persona filters) without requiring an extra click.
  useEffect(() => {
    if (formValues.personaAudienceMode !== 'all_matching') return;
    const t = setTimeout(() => {
      void handlePreviewAudienceRef.current();
    }, 450);
    return () => clearTimeout(t);
  }, [
    tenantId,
    formValues.personaAudienceMode,
    formValues.countryMode,
    formValues.countries,
    formValues.sectorMode,
    formValues.sectors,
    formValues.accountSelectionMode,
    formValues.selectedAccountIds,
    formValues.minAccountFitScore,
    formValues.includeAllArchetypes,
    formValues.archetypes,
    formValues.includeAllDepartments,
    formValues.departments,
    formValues.roleTitleKeywords,
    formValues.useSignals,
    formValues.selectedPersonaIds,
  ]);

  // Toggle persona selection for manual mode
  const togglePersonaSelection = (personId) => {
    const currentSelected = formValues.selectedPersonaIds || [];
    const isSelected = currentSelected.includes(personId);
    
    if (isSelected) {
      updateFormValue('selectedPersonaIds', currentSelected.filter(id => id !== personId));
    } else {
      updateFormValue('selectedPersonaIds', [...currentSelected, personId]);
    }
  };

  // Select all visible personas (account-scoped + search, same as the list)
  const selectAllVisiblePersonas = () => {
    updateFormValue('selectedPersonaIds', filteredManualPersonas.map((p) => p.person_id));
  };

  // Clear all persona selections
  const clearAllPersonaSelections = () => {
    updateFormValue('selectedPersonaIds', []);
  };

  // Select all visible pursuit personas
  const selectAllVisiblePursuitPersonas = () => {
    const allIds = filteredPursuitPersonas.map(p => p.person_id);
    updateFormValue('selectedPersonaIds', allIds);
  };

  const validate = () => {
    const errors = {};
    
    if (!formValues.name.trim()) errors.name = 'Name is required';
    if (!formValues.goalDescription.trim()) {
      errors.goalDescription = 'Goal description is required';
    } else if (formValues.goalDescription.length < 100) {
      errors.goalDescription = `Goal must be at least 100 characters (${formValues.goalDescription.length}/100)`;
    }
    if (formValues.countryMode === 'individual' && (!formValues.countries || formValues.countries.length === 0)) {
      errors.country = 'Select at least one country';
    }
    if (formValues.sectorMode === 'individual' && (!formValues.sectors || formValues.sectors.length === 0)) {
      errors.sector = 'Select at least one sector';
    }
    if (formValues.enabledChannels.length === 0) errors.enabledChannels = 'At least one channel is required';
    if (formValues.steps.length === 0) errors.steps = 'At least one step is required';
    
    if (formValues.accountSelectionMode === 'specific_accounts' && formValues.selectedAccountIds.length === 0) {
      errors.selectedAccountIds = 'Select at least one account';
    }
    if (formValues.accountSelectionMode === 'by_fit_score' && !Number.isFinite(Number(formValues.minAccountFitScore))) {
      errors.minAccountFitScore = 'Minimum fit score is required';
    }
    
    // Add validation for manual persona mode
    if (formValues.personaAudienceMode === 'manual') {
      if (!formValues.selectedPersonaIds || formValues.selectedPersonaIds.length === 0) {
        errors.selectedPersonaIds = 'Please select at least one persona for manual targeting';
      }
    }
    
    formValues.steps.forEach((step, idx) => {
      if (!step.strategicHook.trim()) errors[`step_${idx}_hook`] = 'Strategic hook is required';
      if (!step.messageFocus.trim()) errors[`step_${idx}_focus`] = 'Message focus is required';
    });
    
    setValidationErrors(errors);
    return { valid: Object.keys(errors).length === 0, errors };
  };

  const handleCloneToEdit = useCallback(
    async (playbookId, duplicateEndpoint, cloneSource = 'diy_form') => {
      const pid = playbookId != null ? String(playbookId).trim() : '';
      const tid = tenantId != null ? String(tenantId).trim() : '';
      const uid = effectiveUserId;

      setIsCloning(true);
      try {
        const result = await cloneDiyPlaybook({
          playbookId: pid,
          tenantId: tid,
          userId: uid,
          cloneSource,
          duplicateEndpoint,
          playbookName: initialValues?.name ?? formValues.name ?? null,
          sourceOwnerUserId: initialValues?.owner_user_id ?? null,
        });

        if (!result.ok) {
          if (result.reason === 'missing_context') {
            toast.error('Missing tenant or user context');
          } else if (result.reason === 'missing_new_id') {
            toast.error('Clone succeeded but no playbook id was returned.');
          } else {
            toast.error('Failed to clone playbook. Try again.');
          }
          return;
        }

        navigate(`${createPageUrl('DIYPlaybook')}?id=${encodeURIComponent(result.newPlaybookId)}`);
        toast.success('Playbook cloned. Opening copy…');
      } finally {
        setIsCloning(false);
      }
    },
    [
      tenantId,
      effectiveUserId,
      navigate,
      initialValues?.owner_user_id,
      initialValues?.name,
      formValues.name,
    ],
  );

  const handleSubmit = async (andRun = false) => {
    console.log('FORM handleSubmit called', { andRun });
    if (isReadOnlyPlaybook) {
      toast.error('This playbook belongs to another user.', {
        description: 'Clone it to your account to edit.',
      });
      return;
    }
    const { valid, errors } = validate();
    if (!valid) {
      const firstKey = Object.keys(errors)[0];
      toast.error('Please fix the errors before saving', {
        description: errors[firstKey] || 'Check the form for required fields'
      });
      return;
    }

    try {
      // Sanitize against FULL owner catalog — not diyAlignedAssets (product-filtered pickers).
      // Merging aligned assets on save made kpi_8 invalid when only 2 SUPPORT KPIs were visible.
      const payload = sanitizeDiyFormValuesAgainstCatalog(formValues, filterOptions, {
        logWarnings: true,
      });
      const result = await onSubmit(payload, andRun);
      if (result?.status === 403 && result?.errorData?.error === 'ownership_required') {
        const errData = result.errorData;
        const playbookId = initialValues?.id || formValues.id;
        toast.error('This playbook belongs to another user.', {
          description: 'Clone it to your account to edit. We can do that for you.',
          action: {
            label: 'Clone now',
            onClick: () =>
              handleCloneToEdit(
                playbookId,
                errData.duplicate_endpoint,
                'diy_form_403_toast',
              ),
          },
        });
        return;
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to save', { description: error?.message || 'Please try again' });
    }
  };

  const onSaveOnlyClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('SAVE ONLY button clicked');
    await handleSubmit(false);
  };

  const onSaveAndRunClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('SAVE AND RUN button clicked');
    await handleSubmit(true);
  };

  if (isLoadingOptions) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isReadOnlyPlaybook && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 flex flex-wrap items-center gap-2">
          <span>
            <strong>Read-only:</strong> This playbook was created by another user. Clone it to your
            account to make changes.
          </span>
          <Button
            type="button"
            onClick={() => handleCloneToEdit(initialValues?.id, undefined, 'diy_form_banner')}
            variant="outline"
            size="sm"
            className="shrink-0"
          >
            Clone to my account
          </Button>
        </div>
      )}
      {showAssetStripBanner && (
        <div className="flex items-start justify-between gap-2 rounded-md border border-amber-200 bg-amber-50/90 px-3 py-2 text-sm text-amber-950">
          <p className="flex-1 pr-2">
            Some saved selections from a previous version aren&apos;t available — they&apos;ve been cleared.
          </p>
          <button
            type="button"
            onClick={() => setShowAssetStripBanner(false)}
            className="shrink-0 rounded p-1 text-amber-900 hover:bg-amber-100"
            aria-label="Dismiss notice"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {/* Section A: Basics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Playbook Basics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="name">Playbook name *</Label>
            <Input
              id="name"
              value={formValues.name}
              onChange={(e) => updateFormValue('name', e.target.value)}
              placeholder="e.g., Q1 Champion Outreach"
              className={validationErrors.name ? 'border-red-500' : ''}
            />
            {validationErrors.name && (
              <p className="text-sm text-red-500 mt-1">{validationErrors.name}</p>
            )}
          </div>

          <div>
            <Label htmlFor="goalDescription">Goal of this playbook *</Label>
            <Textarea
              id="goalDescription"
              value={formValues.goalDescription}
              onChange={(e) => updateFormValue('goalDescription', e.target.value)}
              placeholder="Explain what a good outcome looks like, who you care most about, and what you want them to do next..."
              rows={4}
              className={validationErrors.goalDescription ? 'border-red-500' : ''}
            />
            <div className="flex justify-between mt-1">
              <p className="text-xs text-gray-500">
                Explain what a good outcome looks like, who you care most about, and what you want them to do next.
              </p>
              <p className={`text-xs ${formValues.goalDescription.length >= 100 ? 'text-green-600' : 'text-gray-500'}`}>
                {formValues.goalDescription.length}/100
              </p>
            </div>
            {validationErrors.goalDescription && (
              <p className="text-sm text-red-500 mt-1">{validationErrors.goalDescription}</p>
            )}
          </div>

          <div>
            <Label htmlFor="country" className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Country *
            </Label>
            <div className="space-y-3">
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="countryMode"
                    checked={formValues.countryMode === 'all'}
                    onChange={() => {
                      setFormValues(prev => ({
                        ...prev,
                        countryMode: 'all',
                        countries: []
                      }));
                    }}
                    className="w-4 h-4 text-[#00C2C2]"
                  />
                  <span className="text-sm">All countries</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="countryMode"
                    checked={formValues.countryMode === 'individual'}
                    onChange={() => {
                      setFormValues(prev => ({
                        ...prev,
                        countryMode: 'individual'
                      }));
                    }}
                    className="w-4 h-4 text-[#00C2C2]"
                  />
                  <span className="text-sm">Individual countries</span>
                </label>
              </div>

              {formValues.countryMode === 'individual' && (
                <div className="pl-6 border-l-2 border-[#00C2C2] space-y-2 max-h-40 overflow-y-auto">
                  {(filterOptions?.countries || []).map(country => {
                    const isChecked = (formValues.countries || []).includes(country.value);
                    return (
                      <label key={country.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const current = formValues.countries || [];
                            if (e.target.checked) {
                              updateFormValue('countries', [...current, country.value]);
                            } else {
                              updateFormValue('countries', current.filter(c => c !== country.value));
                            }
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-[#00C2C2] focus:ring-[#00C2C2]"
                        />
                        <span className="text-sm">{country.label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            {validationErrors.country && (
              <p className="text-sm text-red-500 mt-1">{validationErrors.country}</p>
            )}
          </div>

          <div>
            <Label className="flex items-center gap-2">Messaging language</Label>
            <div className="space-y-3">
              <div className="space-y-2">
                <label className="flex flex-col gap-1 cursor-pointer">
                  <span className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="messagingLanguageMode"
                      checked={formValues.messagingLanguageMode === 'auto_by_country'}
                      onChange={() => updateFormValue('messagingLanguageMode', 'auto_by_country')}
                      className="w-4 h-4 text-[#00C2C2]"
                    />
                    <span className="text-sm">Use local language by country</span>
                  </span>
                  <p className="text-xs text-gray-500 pl-6">
                    Spanish, Portuguese, French, and English are resolved from the account country using the same
                    default behavior as standard playbooks.
                  </p>
                </label>
                <label className="flex flex-col gap-1 cursor-pointer">
                  <span className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="messagingLanguageMode"
                      checked={formValues.messagingLanguageMode === 'english_only'}
                      onChange={() => updateFormValue('messagingLanguageMode', 'english_only')}
                      className="w-4 h-4 text-[#00C2C2]"
                    />
                    <span className="text-sm">Always use English</span>
                  </span>
                  <p className="text-xs text-gray-500 pl-6">Force all generated messages to be in English.</p>
                </label>
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="sector">Sector *</Label>
            <div className="space-y-3">
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="sectorMode"
                    checked={formValues.sectorMode === 'all'}
                    onChange={() => {
                      setFormValues(prev => ({
                        ...prev,
                        sectorMode: 'all',
                        sectors: []
                      }));
                    }}
                    className="w-4 h-4 text-[#00C2C2]"
                  />
                  <span className="text-sm">All sectors</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="sectorMode"
                    checked={formValues.sectorMode === 'individual'}
                    onChange={() => {
                      setFormValues(prev => ({
                        ...prev,
                        sectorMode: 'individual'
                      }));
                    }}
                    className="w-4 h-4 text-[#00C2C2]"
                  />
                  <span className="text-sm">Individual sectors</span>
                </label>
              </div>

              {formValues.sectorMode === 'individual' && (
                <div className="pl-6 border-l-2 border-[#00C2C2] space-y-2 max-h-40 overflow-y-auto">
                  {(filterOptions?.sectors || []).map(sector => {
                    const isChecked = (formValues.sectors || []).includes(sector.value);
                    return (
                      <label key={sector.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const current = formValues.sectors || [];
                            if (e.target.checked) {
                              updateFormValue('sectors', [...current, sector.value]);
                            } else {
                              updateFormValue('sectors', current.filter(s => s !== sector.value));
                            }
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-[#00C2C2] focus:ring-[#00C2C2]"
                        />
                        <span className="text-sm">{sector.label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            {validationErrors.sector && (
              <p className="text-sm text-red-500 mt-1">{validationErrors.sector}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Section B: Account Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Account Selection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label>Which accounts should this playbook target?</Label>
            
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="accountSelectionMode"
                  checked={formValues.accountSelectionMode === 'all_in_sector'}
                  onChange={() =>
                    setFormValues((prev) => ({
                      ...prev,
                      accountSelectionMode: 'all_in_sector',
                      selectedAccountIds: [],
                    }))
                  }
                  className="w-4 h-4 text-[#00C2C2]"
                />
                <span className="text-sm">All accounts in this sector</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="accountSelectionMode"
                  checked={formValues.accountSelectionMode === 'specific_accounts'}
                  onChange={() => updateFormValue('accountSelectionMode', 'specific_accounts')}
                  className="w-4 h-4 text-[#00C2C2]"
                />
                <span className="text-sm">Only selected accounts</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="accountSelectionMode"
                  checked={formValues.accountSelectionMode === 'by_fit_score'}
                  onChange={() =>
                    setFormValues((prev) => ({
                      ...prev,
                      accountSelectionMode: 'by_fit_score',
                      selectedAccountIds: [],
                      minAccountFitScore: prev.minAccountFitScore ?? 0,
                    }))
                  }
                  className="w-4 h-4 text-[#00C2C2]"
                />
                <span className="text-sm">Accounts with fit score at least...</span>
              </label>
            </div>
          </div>

          {formValues.accountSelectionMode === 'specific_accounts' && (
            <div className="pl-6 border-l-2 border-[#00C2C2]">
              {((formValues.countryMode === 'individual' && (!formValues.countries || formValues.countries.length === 0)) ||
                (formValues.sectorMode === 'individual' && (!formValues.sectors || formValues.sectors.length === 0))) ? (
                <p className="text-sm text-gray-500 italic">Complete country and sector selection first to see available accounts</p>
              ) : isLoadingAccounts ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading accounts...
                </div>
              ) : (
                <div className="space-y-2">
                  <Input
                    type="text"
                    placeholder="Search accounts by name or country..."
                    value={accountSearchTerm}
                    onChange={(e) => setAccountSearchTerm(e.target.value)}
                  />
                  {accountsListMeta.truncated && (
                    <p className="text-xs text-amber-800">
                      {accountsListMeta.total > accountsListMeta.returned
                        ? `Showing ${accountsListMeta.returned} of ${accountsListMeta.total} accounts. Search to find others.`
                        : `Showing the first ${accountsListMeta.returned} accounts. Search to find others.`}
                    </p>
                  )}
                  <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-2">
                  {filteredAccounts.map(account => {
                    const accountId = diyAccountIdForWire(account, tenantId);
                    const accountIdNorm = accountId;
                    const isChecked = Boolean(accountIdNorm && selectedAccountIdSet.has(accountIdNorm));
                    return (
                      <label key={accountIdNorm || String(accountId)} className="flex items-center gap-2 cursor-pointer p-1 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const nid = diyAccountIdForWire(account, tenantId);
                            if (!nid) return;
                            const current = (formValues.selectedAccountIds || []).filter(Boolean);
                            if (e.target.checked) {
                              if (!current.includes(nid)) {
                                updateFormValue('selectedAccountIds', [...current, nid]);
                              }
                            } else {
                              updateFormValue(
                                'selectedAccountIds',
                                current.filter((id) => id !== nid)
                              );
                            }
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-[#00C2C2] focus:ring-[#00C2C2]"
                        />
                        <span className="text-sm flex-1">{account.name || account.account_name}</span>
                        {account.country && (
                          <Badge variant="outline" className="text-xs">
                            {account.country}
                          </Badge>
                        )}
                        {(account.fitScore ?? account.account_fit_score) != null && (
                          <Badge variant="outline" className="text-xs">
                            Fit: {account.fitScore ?? account.account_fit_score}
                          </Badge>
                        )}
                      </label>
                    );
                  })}
                  {accounts.length === 0 && (
                    <p className="text-sm text-gray-500 italic">No accounts found for this sector</p>
                  )}
                  {accounts.length > 0 && filteredAccounts.length === 0 && (
                    <p className="text-sm text-gray-500 italic">No accounts match your search</p>
                  )}
                  </div>
                </div>
              )}
              {validationErrors.selectedAccountIds && (
                <p className="text-sm text-red-500 mt-1">{validationErrors.selectedAccountIds}</p>
              )}
            </div>
          )}

          {formValues.accountSelectionMode === 'by_fit_score' && (
            <div className="pl-6 border-l-2 border-[#00C2C2]">
              <Label htmlFor="minFitScore">Minimum Account Fit Score</Label>
              <Input
                id="minFitScore"
                type="number"
                min={0}
                max={100}
                value={formValues.minAccountFitScore ?? 0}
                onChange={(e) => updateFormValue('minAccountFitScore', e.target.value === '' ? 0 : Number(e.target.value))}
                placeholder="0"
                className={`w-32 ${validationErrors.minAccountFitScore ? 'border-red-500' : ''}`}
              />
              <p className="text-xs text-gray-500 mt-1">
                Only target accounts with Account Fit Score &gt;= this value.
                {marketFitBounds
                  ? marketFitBounds.min === marketFitBounds.max
                    ? ` Scores in this market are ${marketFitBounds.max}.`
                    : ` Scores in this market range from ${marketFitBounds.min} to ${marketFitBounds.max}.`
                  : ''}
              </p>
              {validationErrors.minAccountFitScore && (
                <p className="text-sm text-red-500 mt-1">{validationErrors.minAccountFitScore}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section C: Persona Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5" />
            Persona Selection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Persona audience mode */}
          <div className="space-y-3">
            <Label>Who should receive this playbook?</Label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="personaAudienceMode"
                  checked={formValues.personaAudienceMode === 'all_matching'}
                  onChange={() => updateFormValue('personaAudienceMode', 'all_matching')}
                  className="w-4 h-4 accent-[#00C2C2]"
                />
                <div>
                  <span className="text-sm font-medium">All matching personas</span>
                  <p className="text-xs text-gray-500">
                    Target everyone who matches the filters below, only within the accounts you chose in Account
                    Selection (sector/country scope, specific accounts, or fit-score rule).
                  </p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="personaAudienceMode"
                  checked={formValues.personaAudienceMode === 'pursuit_only'}
                  onChange={() => updateFormValue('personaAudienceMode', 'pursuit_only')}
                  className="w-4 h-4 accent-[#00C2C2]"
                />
                <div>
                  <span className="text-sm font-medium">Pursuit personas only</span>
                  <p className="text-xs text-gray-500">Select from personas in your watchlist</p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="personaAudienceMode"
                  checked={formValues.personaAudienceMode === 'manual'}
                  onChange={() => updateFormValue('personaAudienceMode', 'manual')}
                  className="w-4 h-4 accent-[#00C2C2]"
                />
                <div>
                  <span className="text-sm font-medium">Hand-picked personas</span>
                  <p className="text-xs text-gray-500">
                    Pick individuals from the list (still limited to the accounts selected above; archetype/department
                    filters below do not apply).
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Pursuit persona selection UI */}
          {formValues.personaAudienceMode === 'pursuit_only' && (
            <div className="space-y-3 border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Select Pursuit Personas</Label>
                {(formValues.selectedPersonaIds?.length || 0) > 0 && (
                  <span className="text-xs text-gray-500">
                    {formValues.selectedPersonaIds.length} selected
                  </span>
                )}
              </div>
              
              <p className="text-xs text-gray-500">
                Only watchlist personas at accounts included in Account Selection appear here. Eligible personas are
                selected by default.
              </p>
              
              {isLoadingPursuitPersonas ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Loading pursuit personas...</span>
                  </div>
                </div>
              ) : pursuitPersonas.length === 0 ? (
                <div className="py-6 text-center border-2 border-dashed border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-500">No pursuit personas found</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Add personas to your watchlist first
                  </p>
                </div>
              ) : isLoadingAccounts ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Loading accounts to scope personas…</span>
                  </div>
                </div>
              ) : accountScopedPursuitPersonas.length === 0 ? (
                <div className="py-6 text-center border-2 border-dashed border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-500">No watchlist personas in this account scope</p>
                  <p className="text-xs text-gray-400 mt-1">
                    None of your pursuit personas belong to the accounts selected above. Widen account selection or add
                    contacts at those accounts to your watchlist.
                  </p>
                </div>
              ) : (
                <>
                  {/* Search and action buttons */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex-1">
                      <Input
                        type="text"
                        placeholder="Search by name, role, or account..."
                        value={pursuitPersonaSearchTerm}
                        onChange={(e) => setPursuitPersonaSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={selectAllVisiblePursuitPersonas}
                        className="text-[#00C2C2] hover:bg-[#00C2C2]/10"
                      >
                        Select All
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearAllPersonaSelections}
                        className="text-red-600 hover:bg-red-50"
                      >
                        Clear All
                      </Button>
                    </div>
                  </div>
                  
                  {/* Persona list */}
                  <div className="border rounded-lg max-h-64 overflow-y-auto bg-white">
                    {filteredPursuitPersonas.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        {accountScopedPursuitPersonas.length === 0
                          ? 'No personas in this account scope'
                          : 'No personas match your search'}
                      </div>
                    ) : (
                      filteredPursuitPersonas.map((persona) => {
                        const isSelected = (formValues.selectedPersonaIds || []).includes(persona.person_id);
                        return (
                          <div
                            key={persona.person_id}
                            className={`p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors ${
                              isSelected ? 'bg-blue-50 border-l-4 border-l-[#00C2C2]' : 'border-l-4 border-l-transparent'
                            }`}
                            onClick={() => togglePersonaSelection(persona.person_id)}
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => {}}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm text-gray-900 truncate">
                                    {persona.name || `${persona.first_name} ${persona.last_name}`}
                                  </span>
                                  <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full whitespace-nowrap">
                                    ⭐ Watchlist
                                  </span>
                                  {persona.archetype_name && (
                                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full whitespace-nowrap">
                                      {persona.archetype_name.split(' ')[0]}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 truncate">{persona.role_title}</p>
                                <p className="text-xs text-gray-400 truncate">{persona.account_name}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  
                  {/* Selection summary */}
                  {(formValues.selectedPersonaIds?.length || 0) > 0 && (
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-sm text-gray-600">
                        <strong>{formValues.selectedPersonaIds.length}</strong> persona{formValues.selectedPersonaIds.length !== 1 ? 's' : ''} selected
                      </span>
                      <span className="text-xs text-gray-400">
                        These personas will receive your playbook messages
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Show filters only when in all_matching mode */}
          {formValues.personaAudienceMode === 'all_matching' && (
            <>
              <div className="flex items-center justify-between">
                <Label htmlFor="allArchetypes">Target all archetypes</Label>
                <Switch
                  id="allArchetypes"
                  checked={formValues.includeAllArchetypes}
                  onCheckedChange={(checked) => updateFormValue('includeAllArchetypes', checked)}
                />
              </div>
              
              {!formValues.includeAllArchetypes && (
                <div className="pl-4 border-l-2 border-gray-200 space-y-2">
                  {STANDARD_ARCHETYPES.map(archetype => {
                    const isChecked = (formValues.archetypes || []).includes(archetype);
                    return (
                      <label key={archetype} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            const current = formValues.archetypes || [];
                            if (e.target.checked) {
                              updateFormValue('archetypes', [...current, archetype]);
                            } else {
                              updateFormValue('archetypes', current.filter(a => a !== archetype));
                            }
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-[#00C2C2] focus:ring-[#00C2C2]"
                        />
                        <span className="text-sm">{archetype}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              <div className="flex items-center justify-between">
                <Label htmlFor="allDepartments">Target all departments</Label>
                <Switch
                  id="allDepartments"
                  checked={formValues.includeAllDepartments}
                  onCheckedChange={(checked) => updateFormValue('includeAllDepartments', checked)}
                />
              </div>
              
              {!formValues.includeAllDepartments && (
                <div className="pl-4 border-l-2 border-gray-200 space-y-2 max-h-40 overflow-y-auto">
                  {(filterOptions?.departments || []).length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No departments found for this tenant</p>
                  ) : (
                    (filterOptions?.departments || []).map(dept => {
                      const isChecked = (formValues.departments || []).includes(dept);
                      return (
                        <label key={dept} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const current = formValues.departments || [];
                              if (e.target.checked) {
                                updateFormValue('departments', [...current, dept]);
                              } else {
                                updateFormValue('departments', current.filter(d => d !== dept));
                              }
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-[#00C2C2] focus:ring-[#00C2C2]"
                          />
                          <span className="text-sm">{dept}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="keywords">Role title keywords (optional)</Label>
                  {formValues.roleTitleKeywords.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {formValues.roleTitleKeywords.length} keyword{formValues.roleTitleKeywords.length !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mb-2 min-h-[28px]">
                  {formValues.roleTitleKeywords.map(keyword => (
                    <Badge key={keyword} variant="secondary" className="flex items-center gap-1">
                      {keyword}
                      <button 
                        type="button"
                        onClick={() => removeKeyword(keyword)} 
                        className="hover:text-red-500 ml-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    id="keywords"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={addKeyword}
                    placeholder="Type keyword (e.g., Director, VP)"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (keywordInput.trim() && !formValues.roleTitleKeywords.includes(keywordInput.trim())) {
                        updateFormValue('roleTitleKeywords', [...formValues.roleTitleKeywords, keywordInput.trim()]);
                        setKeywordInput('');
                      }
                    }}
                    disabled={!keywordInput.trim()}
                  >
                    Add
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Filter personas whose job title contains any of these keywords</p>
              </div>
            </>
          )}

          {/* Manual mode persona selection */}
          {formValues.personaAudienceMode === 'manual' && (
            <div className="space-y-3 border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Select Personas</Label>
                {(formValues.selectedPersonaIds?.length || 0) > 0 && (
                  <span className="text-xs text-gray-500">
                    {formValues.selectedPersonaIds.length} selected
                  </span>
                )}
              </div>
              
              <p className="text-xs text-gray-500">
                Only people at accounts included in Account Selection are listed. Use search to filter by name, role,
                or account.
              </p>
              
              {isLoadingManualPersonas ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Loading personas...</span>
                  </div>
                </div>
              ) : availablePersonasForManual.length === 0 ? (
                <div className="py-6 text-center border-2 border-dashed border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-500">No personas found</p>
                  <p className="text-xs text-gray-400 mt-1">
                    No personas are available for this tenant
                  </p>
                </div>
              ) : isLoadingAccounts ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Loading accounts to scope personas…</span>
                  </div>
                </div>
              ) : accountScopedManualPersonas.length === 0 ? (
                <div className="py-6 text-center border-2 border-dashed border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-500">No personas in this account scope</p>
                  <p className="text-xs text-gray-400 mt-1">
                    No loaded personas belong to the accounts selected above. Adjust Account Selection or wait for
                    accounts to finish loading.
                  </p>
                </div>
              ) : (
                <>
                  {/* Search and action buttons */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex-1">
                      <Input
                        type="text"
                        placeholder="Search by name, role, or account..."
                        value={manualPersonaSearchTerm}
                        onChange={(e) => setManualPersonaSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={selectAllVisiblePersonas}
                        className="text-[#00C2C2] hover:bg-[#00C2C2]/10"
                      >
                        Select All
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearAllPersonaSelections}
                        className="text-red-600 hover:bg-red-50"
                      >
                        Clear All
                      </Button>
                    </div>
                  </div>
                  
                  {/* Persona list */}
                  <div className="border rounded-lg max-h-64 overflow-y-auto bg-white">
                    {filteredManualPersonas.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        {accountScopedManualPersonas.length === 0
                          ? 'No personas in this account scope'
                          : 'No personas match your search'}
                      </div>
                    ) : (
                      filteredManualPersonas.map((persona) => {
                        const isSelected = (formValues.selectedPersonaIds || []).includes(persona.person_id);
                        return (
                          <div
                            key={persona.person_id}
                            className={`p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors ${
                              isSelected ? 'bg-blue-50 border-l-4 border-l-[#00C2C2]' : 'border-l-4 border-l-transparent'
                            }`}
                            onClick={() => togglePersonaSelection(persona.person_id)}
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => {}}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm text-gray-900 truncate">
                                    {persona.name || `${persona.first_name} ${persona.last_name}`}
                                  </span>
                                  {persona.in_pursuit && (
                                    <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full whitespace-nowrap">
                                      ⭐ Watchlist
                                    </span>
                                  )}
                                  {persona.archetype_name && (
                                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full whitespace-nowrap">
                                      {persona.archetype_name.split(' ')[0]}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 truncate">{persona.role_title}</p>
                                <p className="text-xs text-gray-400 truncate">{persona.account_name}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  
                  {/* Selection summary */}
                  {(formValues.selectedPersonaIds?.length || 0) > 0 && (
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-sm text-gray-600">
                        <strong>{formValues.selectedPersonaIds.length}</strong> persona{formValues.selectedPersonaIds.length !== 1 ? 's' : ''} selected
                      </span>
                      <span className="text-xs text-gray-400">
                        These personas will receive your playbook messages
                      </span>
                    </div>
                  )}
                </>
              )}
              
              {/* Validation error */}
              {validationErrors?.selectedPersonaIds && (
                <p className="text-sm text-red-500 mt-2">{validationErrors.selectedPersonaIds}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section D: Signals */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Signal Enrichment</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="useSignals">Use live signals to personalize messages</Label>
              <p className="text-xs text-gray-500 mt-1">
                {formValues.useSignals
                  ? 'Messages will include account and persona signals (strategy, procurement, leadership changes, etc.)'
                  : 'Messages will rely only on your goal description and step configuration'}
              </p>
            </div>
            <Switch
              id="useSignals"
              checked={formValues.useSignals}
              onCheckedChange={(checked) => updateFormValue('useSignals', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Section E: Channels and Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Channels & Sequence</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label>Which channels? *</Label>
            <div className="flex gap-4 mt-2">
              {['email', 'linkedin', 'call'].map(channel => {
                const Icon = CHANNEL_ICONS[channel];
                const isEnabled = formValues.enabledChannels.includes(channel);
                const isLastChannel = isEnabled && formValues.enabledChannels.length === 1;
                
                return (
                  <label key={channel} className={`flex items-center gap-2 ${isLastChannel ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      disabled={isLastChannel}
                      onChange={(e) => {
                        if (isLastChannel) return;

                        let newChannels;
                        if (e.target.checked) {
                          newChannels = [...formValues.enabledChannels, channel];
                        } else {
                          newChannels = formValues.enabledChannels.filter(c => c !== channel);
                        }

                        // Update steps that use the disabled channel
                        const updatedSteps = formValues.steps.map(step => {
                          if (!channelMatchesEnabledChannels(step.channel, newChannels)) {
                            return { ...step, channel: newChannels[0] };
                          }
                          return step;
                        });

                        setFormValues(prev => ({
                          ...prev,
                          enabledChannels: newChannels,
                          steps: updatedSteps
                        }));
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-[#00C2C2] focus:ring-[#00C2C2] disabled:opacity-50"
                    />
                    <Icon className="w-4 h-4" />
                    <span className="text-sm capitalize">{channel}</span>
                  </label>
                );
              })}
            </div>
            {validationErrors.enabledChannels && (
              <p className="text-sm text-red-500 mt-1">{validationErrors.enabledChannels}</p>
            )}
          </div>

          <div className="rounded-lg border border-dashed border-gray-200 p-4 space-y-4 bg-gray-50/50">
            <div>
              <h4 className="font-medium text-sm text-[#0A1A2F]">Product alignment</h4>
              <p className="text-xs text-gray-500 mt-1">
                Select at least one product before choosing KPIs, hooks, case studies, product links, or
                educational resources in each step. Only assets tagged for your selection are shown; select every
                product to include library items tagged for all products.
              </p>
            </div>
            {(filterOptions?.settings?.products || []).length === 0 ? (
              <p className="text-xs text-gray-500 italic">
                No products configured in Settings → Personalization.
              </p>
            ) : (
              <>
                <div>
                  <Label className="text-xs">Products for this playbook</Label>
                  <div className="border rounded-md p-2 mt-1 space-y-1 max-h-32 overflow-y-auto">
                    {(filterOptions?.settings?.products || []).map((p) => {
                      const pk = normalizeProductAlignmentTag(p.productKey ?? p.id);
                      const isChecked = (formValues.selectedProductKeys || []).some(
                        (k) => normalizeProductAlignmentTag(k) === pk,
                      );
                      return (
                        <label
                          key={p.id}
                          className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded px-1"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const cur = formValues.selectedProductKeys || [];
                              const normCur = (keys) =>
                                keys
                                  .map((k) => normalizeProductAlignmentTag(k))
                                  .filter((k, i, arr) => k && k !== 'ALL' && arr.indexOf(k) === i);
                              if (e.target.checked) {
                                updateFormValue(
                                  'selectedProductKeys',
                                  normCur([...cur, pk]),
                                );
                              } else {
                                updateFormValue(
                                  'selectedProductKeys',
                                  normCur(cur.filter((k) => normalizeProductAlignmentTag(k) !== pk)),
                                );
                              }
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-[#00C2C2] focus:ring-[#00C2C2]"
                          />
                          <span className="text-sm">{p.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  {hasDiyProductSelection(formValues.selectedProductKeys) ? (
                    <p className="text-xs text-gray-500 mt-1">
                      {formValues.selectedProductKeys.length} product
                      {formValues.selectedProductKeys.length !== 1 ? 's' : ''} selected — assets are available in
                      each step below.
                    </p>
                  ) : (
                    <p className="text-xs text-amber-800 mt-1">
                      Select at least one product to unlock KPIs, hooks, and other assets in your steps.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="space-y-4">
            <Label>Sequence steps</Label>
            {formValues.steps.map((step, idx) => {
              const isExpanded = expandedSteps[step.id];
              
              return (
                <div key={step.id} className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium">Touch {step.order}</h4>
                      <LinkedInSubtypeBadge
                        source={{
                          channel: resolveDiyStepChannelFromApiStep(step),
                          allSteps: formValues.steps,
                          stepIndex: idx,
                        }}
                        debugContext={`diy-step-${idx + 1}`}
                      />
                    </div>
                    {formValues.steps.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeStep(step.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Channel</Label>
                      <Select
                        value={normalizeLinkedInStepChannelValue(
                          step.channel,
                          idx,
                          formValues.steps,
                          step,
                        )}
                        onValueChange={(v) => updateStep(step.id, 'channel', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {buildDiyStepChannelSelectOptions(formValues.enabledChannels, {
                            steps: formValues.steps,
                            stepId: step.id,
                          }).map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {isLinkedInOutboxChannel({ channel: step.channel }) &&
                        canOfferLinkedInConnectionRequest(formValues.steps, step.id) && (
                          <p className="text-xs text-gray-500 mt-1">
                            First LinkedIn touch: choose connection request or direct message.
                            Later LinkedIn steps are direct message only.
                          </p>
                        )}
                      {isLinkedInOutboxChannel({ channel: step.channel }) &&
                        !canOfferLinkedInConnectionRequest(formValues.steps, step.id) && (
                          <p className="text-xs text-gray-500 mt-1">
                            Direct message only — connection request must be the first LinkedIn
                            step in this sequence.
                          </p>
                        )}
                    </div>
                    <div>
                      <Label>Day offset</Label>
                      <Input
                        type="number"
                        min={0}
                        value={step.dayOffset}
                        onChange={(e) => updateStep(step.id, 'dayOffset', Number(e.target.value))}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Strategic hook *</Label>
                    <Input
                      value={step.strategicHook}
                      onChange={(e) => updateStep(step.id, 'strategicHook', e.target.value)}
                      placeholder="What's the angle for this touch?"
                      className={validationErrors[`step_${idx}_hook`] ? 'border-red-500' : ''}
                    />
                    {validationErrors[`step_${idx}_hook`] && (
                      <p className="text-sm text-red-500 mt-1">{validationErrors[`step_${idx}_hook`]}</p>
                    )}
                  </div>

                  <div>
                    <Label>Message focus *</Label>
                    <Input
                      value={step.messageFocus}
                      onChange={(e) => updateStep(step.id, 'messageFocus', e.target.value)}
                      placeholder="One key point - don't overload"
                      className={validationErrors[`step_${idx}_focus`] ? 'border-red-500' : ''}
                    />
                    {validationErrors[`step_${idx}_focus`] && (
                      <p className="text-sm text-red-500 mt-1">{validationErrors[`step_${idx}_focus`]}</p>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => setExpandedSteps(prev => ({ ...prev, [step.id]: !isExpanded }))}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                  >
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    Optional enhancements
                  </button>

                  {isExpanded && (
                    <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                    <div>
                      <Label className="text-xs">KPI to highlight</Label>
                      <Select
                        value={step.kpiKey || '_none_'}
                        onValueChange={(v) => updateStep(step.id, 'kpiKey', v === '_none_' ? null : v)}
                        disabled={productGateBlocks}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none_">None</SelectItem>
                          {(diyAlignedAssets?.kpis || []).map(kpi => (
                            <SelectItem key={kpi.id} value={kpi.id}>{kpi.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <DiyProductAlignmentFilterHint
                        selectedProductKeys={formValues.selectedProductKeys}
                        filteredCount={(diyAlignedAssets?.kpis || []).length}
                        totalCount={(filterOptions?.settings?.kpis || []).length}
                        label="KPIs"
                        tenantHasProducts={tenantHasProducts}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Saved hook</Label>
                      <Select
                        value={step.hookKey || '_none_'}
                        onValueChange={(v) => updateStep(step.id, 'hookKey', v === '_none_' ? null : v)}
                        disabled={productGateBlocks}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none_">None</SelectItem>
                          {(diyAlignedAssets?.hooks || []).map(hook => (
                            <SelectItem key={hook.id} value={hook.id}>{hook.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <DiyProductAlignmentFilterHint
                        selectedProductKeys={formValues.selectedProductKeys}
                        filteredCount={(diyAlignedAssets?.hooks || []).length}
                        totalCount={(filterOptions?.settings?.hooks || []).length}
                        label="hooks"
                        tenantHasProducts={tenantHasProducts}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Case study / Client reference</Label>
                      <Select
                        value={step.caseStudyKey || '_none_'}
                        onValueChange={(v) => updateStep(step.id, 'caseStudyKey', v === '_none_' ? null : v)}
                        disabled={productGateBlocks}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none_">None</SelectItem>
                          {(diyAlignedAssets?.caseStudies || []).map((cs) => {
                            const isClientRef = String(cs.type || 'case_study').toLowerCase() === 'client_reference';
                            return (
                              <SelectItem key={cs.id} value={cs.id}>
                                <span className="inline-flex items-center gap-1.5">
                                  <span aria-hidden>{isClientRef ? '🏫' : '📄'}</span>
                                  <span>{cs.label}</span>
                                  <span className="text-[10px] text-gray-500 ml-1">
                                    {isClientRef ? 'Client ref' : 'Case study'}
                                  </span>
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <DiyProductAlignmentFilterHint
                        selectedProductKeys={formValues.selectedProductKeys}
                        filteredCount={(diyAlignedAssets?.caseStudies || []).length}
                        totalCount={(filterOptions?.settings?.caseStudies || []).length}
                        label="case studies / client references"
                        tenantHasProducts={tenantHasProducts}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Product links</Label>
                      <div className="border rounded-md p-2 mt-1 space-y-1 max-h-32 overflow-y-auto">
                        {(diyAlignedAssets?.productLinks || []).length === 0 ? (
                          <p className="text-xs text-gray-500 italic">
                            {productGateBlocks
                              ? 'Select a product above to choose product links.'
                              : 'No product links for the selected product(s).'}
                          </p>
                        ) : (
                          (diyAlignedAssets?.productLinks || []).map(pl => {
                            const canon = canonicalProductLinkName(pl);
                            const isChecked = canon
                              ? (step.productLinkKeys || []).some((k) => {
                                  const t = String(k).trim();
                                  const resolved = productLinkAliasToCanonical.get(t) ?? t;
                                  return resolved === canon;
                                })
                              : false;
                            return (
                              <label
                                key={pl.id}
                                className={`flex items-center gap-2 rounded px-1 ${
                                  canon ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed opacity-60'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  disabled={!canon}
                                  checked={isChecked}
                                  onChange={(e) => {
                                    if (!canon) return;
                                    const current = resolveProductLinkKeysToCanonical(
                                      step.productLinkKeys || [],
                                      productLinkAliasToCanonical,
                                      validProductLinkNames
                                    );
                                    if (e.target.checked) {
                                      updateStep(step.id, 'productLinkKeys', [...new Set([...current, canon])]);
                                    } else {
                                      updateStep(
                                        step.id,
                                        'productLinkKeys',
                                        current.filter((x) => x !== canon)
                                      );
                                    }
                                  }}
                                  className="w-4 h-4 rounded border-gray-300 text-[#00C2C2] focus:ring-[#00C2C2]"
                                />
                                <span className="text-sm">{pl.label}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      <DiyProductAlignmentFilterHint
                        selectedProductKeys={formValues.selectedProductKeys}
                        filteredCount={(diyAlignedAssets?.productLinks || []).length}
                        totalCount={(filterOptions?.settings?.productLinks || []).length}
                        label="product links"
                        tenantHasProducts={tenantHasProducts}
                      />
                      {(step.productLinkKeys || []).length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">{step.productLinkKeys.length} selected</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">{t('educationalResources')}</Label>
                      <p className="text-xs text-gray-500 mt-0.5 mb-1">{t('educationalResourcesHelp')}</p>
                      <DiyProductAlignmentFilterHint
                        selectedProductKeys={formValues.selectedProductKeys}
                        filteredCount={educationalResourcesForPicker.length}
                        totalCount={catalogEducationalResourceCount}
                        label="educational resources"
                        tenantHasProducts={tenantHasProducts}
                      />
                      <div
                        className={`border rounded-md p-2 mt-1 space-y-1 max-h-40 overflow-y-auto ${
                          productGateBlocks || educationalResourcesForPicker.length === 0
                            ? 'opacity-60 pointer-events-none'
                            : ''
                        }`}
                      >
                        {educationalResourcesForPicker.length === 0 ? (
                          <p className="text-xs text-gray-500 italic">
                            {productGateBlocks
                              ? 'Select a product above to choose educational resources.'
                              : t('noEducationalResourcesYet')}
                          </p>
                        ) : (
                          educationalResourcesForPicker.map((row, erIdx) => {
                            const optValue = educationalResourceOptionValue(row, erIdx);
                            const label =
                              String(row.title ?? row.name ?? row.url ?? `Resource ${erIdx + 1}`).trim() ||
                              `Resource ${erIdx + 1}`;
                            const sub =
                              row.product_context != null && String(row.product_context).trim() !== ''
                                ? String(row.product_context).trim()
                                : null;
                            const keys = step.educationalResourceKeys || [];
                            const isChecked = keys.some((k) =>
                              educationalResourceKeyMatchesRow(k, row, erIdx)
                            );
                            return (
                              <label
                                key={`${optValue}-${erIdx}`}
                                className="flex items-center gap-2 rounded px-1 cursor-pointer hover:bg-gray-50"
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    const current = [...(step.educationalResourceKeys || [])];
                                    if (e.target.checked) {
                                      const next = [...new Set([...current, optValue])];
                                      updateStep(step.id, 'educationalResourceKeys', next);
                                    } else {
                                      updateStep(
                                        step.id,
                                        'educationalResourceKeys',
                                        current.filter(
                                          (k) => !educationalResourceKeyMatchesRow(k, row, erIdx)
                                        )
                                      );
                                    }
                                  }}
                                  className="w-4 h-4 rounded border-gray-300 text-[#00C2C2] focus:ring-[#00C2C2]"
                                />
                                <span className="text-sm flex-1 min-w-0">
                                  {label}
                                  {sub && (
                                    <span className="ml-1 text-xs text-gray-500">({sub})</span>
                                  )}
                                </span>
                              </label>
                            );
                          })
                        )}
                      </div>
                      {(step.educationalResourceKeys || []).length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {step.educationalResourceKeys.length} selected
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">CTA style</Label>
                      <Select
                        value={step.ctaStyleKey || '_none_'}
                        onValueChange={(v) => updateStep(step.id, 'ctaStyleKey', v === '_none_' ? null : v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none_">None</SelectItem>
                          {(filterOptions?.ctaStyles || []).map(cta => (
                            <SelectItem key={cta.id} value={cta.id}>{cta.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    </div>
                  )}
                </div>
              );
            })}

            <Button
              type="button"
              variant="outline"
              onClick={addStep}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Step
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Section F: Sending Behaviour */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Sending Behaviour</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Label htmlFor="autoSchedule">Automatically schedule and send emails</Label>
                {formValues.autoScheduleEmails && (
                  <Badge variant="outline" className="text-yellow-700 bg-yellow-50 border-yellow-300">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Auto-send enabled
                  </Badge>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                If enabled, emails will be scheduled using each step's day offset and sent without manual review.
                LinkedIn and phone steps remain manual tasks.
              </p>
            </div>
            <Switch
              id="autoSchedule"
              checked={formValues.autoScheduleEmails}
              onCheckedChange={(checked) => updateFormValue('autoScheduleEmails', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Audience Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Audience Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={handlePreviewAudience}
              disabled={isLoadingPreview}
            >
              {isLoadingPreview ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Preview audience
                </>
              )}
            </Button>

            {audiencePreview && (
              <div className="space-y-1">
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <Building2 className="w-4 h-4 text-gray-500" />
                    <span className="font-medium">{audiencePreview.accountsCount}</span>
                    <span className="text-gray-500">accounts</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4 text-gray-500" />
                    <span className="font-medium">{audiencePreview.personasCount}</span>
                    <span className="text-gray-500">personas</span>
                  </div>
                </div>
                {audienceEmptyReason ? (
                  <p className="text-xs text-amber-800">{audienceEmptyReason}</p>
                ) : null}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Form Actions — explicit type=button + dedicated handlers (no wrapping <form>) */}
      <div className="flex flex-wrap justify-end gap-3 pt-4 border-t">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSaving || isCloning}>
            Cancel
          </Button>
        )}
        {initialValues?.id && !isReadOnlyPlaybook && (
          <Button
            type="button"
            variant="outline"
            onClick={() => handleCloneToEdit(initialValues.id, undefined, 'diy_form_actions')}
            disabled={isSaving || isCloning}
            title="Duplicate this playbook including all step assets"
          >
            {isCloning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />}
            {t('clone')}
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={onSaveOnlyClick}
          disabled={isSaving || isCloning || isReadOnlyPlaybook}
        >
          {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Save playbook
        </Button>
        <Button
          type="button"
          onClick={onSaveAndRunClick}
          disabled={isSaving || isCloning || isReadOnlyPlaybook}
          className="bg-[#00C2C2] hover:bg-[#00A3A3] min-w-[10rem]"
        >
          {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Save and run
        </Button>
      </div>
    </div>
  );
}